import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTrash, faPlus, faEdit, faSave, faImage, 
    faConciergeBell, faLayerGroup, faTimes, faCloudUploadAlt 
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';

const AdminServices = ({ list, refresh }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({ name: '', price: '', description: '', category_id: '' });
    const [categories, setCategories] = useState([]);
    const [file, setFile] = useState(null);
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(false);

    const getHeaders = useCallback((isMultipart = false) => {
        const headers = {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'ngrok-skip-browser-warning': 'true'
        };
        if (!isMultipart) {
            headers['Content-Type'] = 'application/json';
        }
        return headers;
    }, []);

    useEffect(() => {
        fetch(`${API_BASE}/api/categories`, { headers: getHeaders() })
            .then(res => res.json())
            .then(data => setCategories(Array.isArray(data) ? data : []))
            .catch(err => console.error("Categories load error:", err));
    }, [getHeaders]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const selectedCat = categories.find(cat => String(cat.id) === String(formData.category_id));
        const categoryName = selectedCat ? selectedCat.name : '';

        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('price', formData.price);
            data.append('description', formData.description);
            data.append('category_id', formData.category_id);
            data.append('category_name', categoryName);
            
            if (file) data.append('image', file);

            const method = editId ? 'PUT' : 'POST';
            const url = editId ? `${API_BASE}/api/subservices/${editId}` : `${API_BASE}/api/subservices`;

            const res = await fetch(url, {
                method: method,
                headers: getHeaders(true),
                body: data
            });

            if (res.ok) {
                setFormData({ name: '', price: '', description: '', category_id: '' });
                setFile(null);
                setEditId(null);
                refresh();
            } else if (res.status === 401) {
                alert(t('error_session_expired'));
            }
        } catch (err) {
            console.error("Service save error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setEditId(item.id);
        setFormData({
            name: item.name,
            price: item.price || '',
            description: item.description || '',
            category_id: item.category_id || ''
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('confirm_delete_service'))) return;
        try {
            const res = await fetch(`${API_BASE}/api/subservices/${id}`, { 
                method: 'DELETE', 
                headers: getHeaders() 
            });
            if (res.ok) refresh();
        } catch (err) { 
            console.error(err); 
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.analyticsRow}>
                <div style={{...styles.anaCard, background: 'linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)'}}>
                    <div style={styles.anaIconBox}><FontAwesomeIcon icon={faConciergeBell} /></div>
                    <div>
                        <p style={styles.anaLabel}>{t('ana_services')}</p>
                        <h3 style={styles.anaVal}>{list?.length || 0}</h3>
                    </div>
                </div>
                <div style={{...styles.anaCard, background: 'linear-gradient(135deg, #FFB547 0%, #FF9100 100%)'}}>
                    <div style={styles.anaIconBox}><FontAwesomeIcon icon={faLayerGroup} /></div>
                    <div>
                        <p style={styles.anaLabel}>{t('ana_categories')}</p>
                        <h3 style={styles.anaVal}>{categories.length}</h3>
                    </div>
                </div>
            </div>

            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <div style={styles.iconBox}>
                        <FontAwesomeIcon icon={editId ? faEdit : faPlus} />
                    </div>
                    <h3 style={styles.cardTitle}>{editId ? t('edit_service') : t('add_service')}</h3>
                </div>

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.gridRow}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>{t('label_category')}</label>
                            <select 
                                style={styles.input} 
                                value={formData.category_id} 
                                onChange={e => setFormData({...formData, category_id: e.target.value})}
                                required
                            >
                                <option value="">{t('select_option')}</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>{t('label_service_name')}</label>
                            <input 
                                style={styles.input}
                                placeholder={t('placeholder_service_name')} 
                                value={formData.name} 
                                onChange={e => setFormData({...formData, name: e.target.value})} 
                                required 
                            />
                        </div>
                    </div>

                    <div style={styles.gridRow}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>{t('label_price')}</label>
                            <input 
                                style={styles.input}
                                placeholder={t('placeholder_price')} 
                                value={formData.price} 
                                onChange={e => setFormData({...formData, price: e.target.value})} 
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>{t('label_image')}</label>
                            <label style={styles.fileLabel}>
                                <FontAwesomeIcon icon={faCloudUploadAlt} /> {file ? file.name : ` ${t('upload_image')}`}
                                <input type="file" hidden onChange={e => setFile(e.target.files[0])} accept="image/*" />
                            </label>
                        </div>
                    </div>

                    <div style={styles.inputGroup}>
                        <label style={styles.label}>{t('label_description')}</label>
                        <textarea 
                            style={styles.textarea}
                            placeholder={t('placeholder_description')} 
                            value={formData.description} 
                            onChange={e => setFormData({...formData, description: e.target.value})} 
                            rows="3"
                        />
                    </div>

                    <div style={styles.btnRow}>
                        <button type="submit" style={styles.saveBtn} disabled={loading}>
                            <FontAwesomeIcon icon={faSave} style={{marginRight: '8px'}} />
                            {loading ? t('btn_processing') : (editId ? t('btn_update') : t('btn_save'))}
                        </button>
                        {editId && (
                            <button type="button" onClick={() => {setEditId(null); setFormData({name:'', price:'', description:'', category_id:''}); setFile(null);}} style={styles.cancelBtn}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <div style={{...styles.card, marginTop: '30px'}}>
                <h3 style={{...styles.cardTitle, marginBottom: '20px'}}>{t('active_services_title')}</h3>
                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.thRow}>
                                <th style={styles.th}>{t('table_image')}</th>
                                <th style={styles.th}>{t('table_service')}</th>
                                <th style={styles.th}>{t('table_category')}</th>
                                <th style={styles.th}>{t('table_price')}</th>
                                <th style={{...styles.th, textAlign: 'center'}}>{t('table_actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list && list.length > 0 ? list.map(item => (
                                <tr key={item.id} style={styles.tr}>
                                    <td style={styles.td}>
                                        <div style={styles.imgContainer}>
                                            {item.image ? (
                                               <img 
    src={item.image ? `${API_BASE}${item.image}` : '/assets/img/no-image.png'} 
    alt={item.name || "thumbnail"} 
    style={styles.tableImg} 
    loading="lazy"       // 1. Բեռնել միայն այն տողերը, որոնք այդ պահին երևում են էկրանին
    decoding="async"     // 2. Թույլ տալ բրաուզերին ֆոնում մշակել նկարը
    onError={(e) => { 
        e.target.onerror = null; 
        e.target.src = 'https://via.placeholder.com/50?text=No+Img'; 
    }} 
/>
                                            ) : (
                                                <div style={styles.noImg}><FontAwesomeIcon icon={faImage} /></div>
                                            )}
                                        </div>
                                    </td>
                                    <td style={styles.tdTitle}>{item.name}</td>
                                    <td style={styles.tdCat}>
                                        <span style={styles.catBadge}>{item.category_name || '---'}</span>
                                    </td>
                                    <td style={styles.tdPrice}>{item.price ? `${item.price} ֏` : t('price_negotiable')}</td>
                                    <td style={styles.tdActions}>
                                        <button onClick={() => handleEdit(item)} style={styles.editBtn}><FontAwesomeIcon icon={faEdit} /></button>
                                        <button onClick={() => handleDelete(item.id)} style={styles.deleteBtn}><FontAwesomeIcon icon={faTrash} /></button>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="5" style={{textAlign:'center', padding:'20px', color:'#A3AED0'}}>{t('no_data')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ... styles object remains the same ...
// --- STYLES (նույնն են մնացել) ---
const styles = {
    container: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
    analyticsRow: { display: 'flex', gap: '20px', marginBottom: '30px' },
    anaCard: { flex: 1, padding: '20px', borderRadius: '20px', color: '#fff', display: 'flex', alignItems: 'center', gap: '15px' },
    anaIconBox: { width: '45px', height: '45px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' },
    anaLabel: { fontSize: '11px', margin: 0, opacity: 0.8, textTransform: 'uppercase' },
    anaVal: { fontSize: '22px', margin: 0, fontWeight: '800' },
    card: { background: '#fff', borderRadius: '24px', padding: '25px', boxShadow: '0 10px 30px rgba(0,0,0,0.02)', border: '1px solid #f1f5f9' },
    cardHeader: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' },
    iconBox: { width: '40px', height: '40px', borderRadius: '12px', background: '#4318FF', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    cardTitle: { fontSize: '18px', fontWeight: '800', color: '#1B2559', margin: 0 },
    form: { display: 'flex', flexDirection: 'column', gap: '15px' },
    gridRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
    inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    label: { fontSize: '12px', fontWeight: '700', color: '#1B2559', marginLeft: '5px' },
    input: { padding: '12px', borderRadius: '12px', border: '1px solid #E0E5F2', background: '#F4F7FE', outline: 'none' },
    textarea: { padding: '12px', borderRadius: '12px', border: '1px solid #E0E5F2', background: '#F4F7FE', outline: 'none', resize: 'vertical' },
    fileLabel: { padding: '12px', borderRadius: '12px', border: '2px dashed #E0E5F2', background: '#F4F7FE', color: '#707EAE', cursor: 'pointer', textAlign: 'center', fontSize: '13px' },
    btnRow: { display: 'flex', gap: '10px' },
    saveBtn: { flex: 1, background: 'linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)', color: '#fff', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' },
    cancelBtn: { width: '50px', background: '#F4F7FE', color: '#1B2559', border: 'none', borderRadius: '12px', cursor: 'pointer' },
    tableWrapper: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse' },
    thRow: { borderBottom: '1px solid #F4F7FE' },
    th: { padding: '12px', textAlign: 'left', color: '#A3AED0', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase' },
    tr: { borderBottom: '1px solid #F4F7FE' },
    td: { padding: '12px' },
    imgContainer: { width: '45px', height: '45px', borderRadius: '10px', overflow: 'hidden', background: '#F4F7FE' },
    tableImg: { width: '100%', height: '100%', objectFit: 'cover' },
    noImg: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#A3AED0' },
    tdTitle: { padding: '12px', fontWeight: '700', color: '#1B2559', fontSize: '14px' },
    tdCat: { padding: '12px' },
    catBadge: { padding: '4px 10px', background: '#F4F7FE', color: '#4318FF', borderRadius: '8px', fontSize: '12px', fontWeight: '600' },
    tdPrice: { padding: '12px', fontWeight: '800', color: '#01B574' },
    tdActions: { display: 'flex', gap: '8px', justifyContent: 'center', padding: '15px 0' },
    editBtn: { background: '#E0E7FF', color: '#4318FF', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer' },
    deleteBtn: { background: '#FEE2E2', color: '#EF4444', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer' }
};

export default AdminServices;